import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Luggage, Truck, Clock, Shield, CheckCircle, AlertTriangle } from "lucide-react";

const TransportBagagesFranceMaroc = () => {
  return (
    <>
      <Helmet>
        <title>Transport de Bagages France Maroc : Guide Pratique 2025 | GoBledPro</title>
        <meta 
          name="description" 
          content="Guide complet pour le transport de bagages entre la France et le Maroc : valises, effets personnels, déménagement léger. Options, tarifs et conseils pratiques." 
        />
        <link rel="canonical" href="https://gobledpro.com/transport-bagages-france-maroc" />
        <meta property="og:title" content="Transport de Bagages France Maroc : Guide Pratique 2025 | GoBledPro" />
        <meta property="og:description" content="Guide complet pour le transport de bagages entre la France et le Maroc : valises, effets personnels, déménagement léger." />
        <meta property="og:image" content="https://gobledpro.com/og-transport-bagages.png" />
        <meta property="og:url" content="https://gobledpro.com/transport-bagages-france-maroc" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="GoBledPro" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://gobledpro.com/og-transport-bagages.png" />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-background pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block bg-primary/10 text-primary text-sm font-medium px-4 py-1.5 rounded-full mb-6">
                Guide Informatif
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6 leading-tight">
                Transport de Bagages France – Maroc : Guide Pratique
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Tout ce qu'il faut savoir pour envoyer vos valises, effets personnels et bagages 
                entre la France et le Maroc en toute sérénité.
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto prose prose-lg prose-headings:font-heading prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground">
              
              {/* Introduction */}
              <section className="mb-12">
                <p className="text-lg leading-relaxed">
                  Que ce soit pour un séjour prolongé au Maroc, un retour définitif au pays, 
                  un déménagement léger ou simplement l'envoi de valises supplémentaires, 
                  le transport de bagages entre la France et le Maroc répond à un besoin 
                  récurrent de la diaspora. Ce guide vous aide à comprendre vos options 
                  et à organiser votre envoi.
                </p>
              </section>

              {/* Section 1 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Luggage className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Qu'entend-on par « bagages » ?</h2>
                </div>
                
                <p>
                  Dans le contexte du transport France-Maroc, le terme « bagages » recouvre 
                  plusieurs types d'envois :
                </p>
                
                <ul className="space-y-3 mt-4">
                  <li>
                    <strong>Valises et sacs de voyage :</strong> Vêtements, articles personnels, 
                    souvenirs, cadeaux pour la famille.
                  </li>
                  <li>
                    <strong>Effets personnels :</strong> Livres, appareils électroniques personnels, 
                    objets de décoration, ustensiles.
                  </li>
                  <li>
                    <strong>Déménagement léger :</strong> Mobilier de petite taille, cartons 
                    de vêtements, linge de maison.
                  </li>
                  <li>
                    <strong>Articles saisonniers :</strong> Équipements d'été, jouets pour 
                    les vacances des enfants, matériel de loisirs.
                  </li>
                </ul>
              </section>

              {/* Section 2 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Options de transport pour vos bagages</h2>
                </div>
                
                <h3 className="text-xl font-semibold mt-6 mb-3">Transport routier groupé</h3>
                <p>
                  C'est l'option la plus courante pour les bagages. Vos valises et cartons 
                  voyagent avec d'autres envois dans un camion, ce qui permet de mutualiser 
                  les coûts. Idéal pour les volumes moyens (1 à 10 valises).
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3">Transport dédié</h3>
                <p>
                  Pour les déménagements plus conséquents ou les envois nécessitant une 
                  attention particulière, certains transporteurs proposent des solutions 
                  dédiées avec collecte à domicile.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3">Bagages accompagnés (avion)</h3>
                <p>
                  Si vous voyagez par avion, vous pouvez acheter des franchises de bagages 
                  supplémentaires. Cette option est rapide mais coûteuse pour de gros volumes. 
                  Elle convient aux envois urgents de 1 à 3 valises.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3">Fret maritime</h3>
                <p>
                  Pour les déménagements importants (plusieurs mètres cubes), le transport 
                  maritime offre des tarifs compétitifs au volume. Les délais sont plus longs 
                  (2 à 4 semaines) mais l'économie peut être significative.
                </p>
              </section>

              {/* Section 3 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Délais et tarification</h2>
                </div>
                
                <h3 className="text-xl font-semibold mt-6 mb-3">Délais moyens</h3>
                <div className="bg-secondary/20 rounded-lg p-6 not-prose mt-4">
                  <div className="grid gap-4">
                    <div className="flex justify-between items-center border-b border-border/50 pb-3">
                      <span className="text-foreground font-medium">Transport routier groupé</span>
                      <span className="text-muted-foreground">7 à 14 jours</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-border/50 pb-3">
                      <span className="text-foreground font-medium">Transport dédié</span>
                      <span className="text-muted-foreground">5 à 10 jours</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-border/50 pb-3">
                      <span className="text-foreground font-medium">Fret aérien</span>
                      <span className="text-muted-foreground">3 à 5 jours</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-foreground font-medium">Fret maritime</span>
                      <span className="text-muted-foreground">14 à 28 jours</span>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-semibold mt-6 mb-3">Facteurs influençant le prix</h3>
                <ul className="space-y-2 mt-4">
                  <li><strong>Volume et poids :</strong> Facturation au kilo ou au mètre cube</li>
                  <li><strong>Destination :</strong> Les villes principales sont mieux desservies</li>
                  <li><strong>Saison :</strong> Pics tarifaires en été et pendant les fêtes</li>
                  <li><strong>Services additionnels :</strong> Collecte à domicile, assurance, livraison</li>
                </ul>
              </section>

              {/* Section 4 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Préparer vos bagages pour l'envoi</h2>
                </div>
                
                <h3 className="text-xl font-semibold mt-6 mb-3">Emballage recommandé</h3>
                <ul className="space-y-3 mt-4">
                  <li>
                    <strong>Valises rigides :</strong> Idéales pour les vêtements et objets fragiles, 
                    elles protègent mieux pendant la manutention.
                  </li>
                  <li>
                    <strong>Cartons renforcés :</strong> Pour les livres, ustensiles et objets lourds, 
                    utilisez des cartons double cannelure.
                  </li>
                  <li>
                    <strong>Film plastique :</strong> Enveloppez vos valises de film étirable pour 
                    les protéger de la poussière et des éraflures.
                  </li>
                  <li>
                    <strong>Cadenas :</strong> Sécurisez vos valises avec des cadenas solides.
                  </li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3">Étiquetage</h3>
                <p>
                  Chaque bagage doit porter une étiquette lisible avec :
                </p>
                <ul className="space-y-2 mt-4">
                  <li>Nom et prénom du destinataire</li>
                  <li>Adresse complète au Maroc</li>
                  <li>Numéro de téléphone marocain</li>
                  <li>Numéro de téléphone en France (pour le transporteur)</li>
                </ul>
              </section>

              {/* Section 5 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Ce qu'il faut éviter</h2>
                </div>
                
                <p>
                  Pour éviter les complications à la douane et pendant le transport, 
                  ne mettez pas dans vos bagages :
                </p>
                
                <ul className="space-y-3 mt-4">
                  <li>
                    <strong>Objets de valeur non déclarés :</strong> Bijoux, argent liquide, 
                    appareils électroniques coûteux sans facture.
                  </li>
                  <li>
                    <strong>Denrées périssables :</strong> Aliments frais, produits laitiers, 
                    viandes (sauf exceptions réglementées).
                  </li>
                  <li>
                    <strong>Liquides en grande quantité :</strong> Parfums, cosmétiques 
                    (limitations pour le transport aérien).
                  </li>
                  <li>
                    <strong>Articles contrefaits :</strong> Risque de saisie à la douane marocaine.
                  </li>
                  <li>
                    <strong>Médicaments sans ordonnance :</strong> Quantités limitées et 
                    ordonnance recommandée pour les traitements.
                  </li>
                </ul>
              </section>

              {/* Section 6 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Assurance et suivi</h2>
                </div>
                
                <h3 className="text-xl font-semibold mt-6 mb-3">L'importance de l'assurance</h3>
                <p>
                  Pour les bagages contenant des objets de valeur ou irremplaçables, 
                  l'assurance transport est fortement recommandée. Elle couvre généralement :
                </p>
                <ul className="space-y-2 mt-4">
                  <li>La perte totale ou partielle</li>
                  <li>Les dommages pendant le transport</li>
                  <li>Le vol (selon les conditions du contrat)</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3">Suivi de l'envoi</h3>
                <p>
                  Privilégiez les transporteurs offrant un suivi de vos bagages. 
                  Un bon suivi permet de :
                </p>
                <ul className="space-y-2 mt-4">
                  <li>Connaître la position de vos bagages en temps réel</li>
                  <li>Anticiper la date de livraison</li>
                  <li>Réagir rapidement en cas de problème</li>
                </ul>
              </section>

              {/* Cas d'usage */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-6">Situations courantes</h2>
                
                <div className="grid gap-6 not-prose">
                  <div className="bg-secondary/20 rounded-xl p-6">
                    <h3 className="font-semibold text-foreground mb-2">Vacances d'été au Maroc</h3>
                    <p className="text-muted-foreground text-sm">
                      Vous partez en famille pour l'été et souhaitez envoyer vêtements, jouets 
                      et équipements de plage en avance. Le transport routier groupé est idéal : 
                      envoyez vos valises 2 semaines avant votre départ pour les retrouver sur place.
                    </p>
                  </div>
                  
                  <div className="bg-secondary/20 rounded-xl p-6">
                    <h3 className="font-semibold text-foreground mb-2">Installation au Maroc</h3>
                    <p className="text-muted-foreground text-sm">
                      Vous déménagez pour vous installer au Maroc. Pour plusieurs valises et cartons, 
                      combinez transport routier pour le gros du volume et bagages accompagnés 
                      en avion pour les effets dont vous aurez besoin immédiatement.
                    </p>
                  </div>
                  
                  <div className="bg-secondary/20 rounded-xl p-6">
                    <h3 className="font-semibold text-foreground mb-2">Cadeaux pour la famille</h3>
                    <p className="text-muted-foreground text-sm">
                      Vous voulez envoyer des cadeaux à la famille sans vous déplacer. 
                      Les services de collecte à domicile permettent d'expédier 1 à 3 valises 
                      sans avoir à vous déplacer en point de dépôt.
                    </p>
                  </div>
                </div>
              </section>

              {/* Conclusion */}
              <section className="bg-secondary/20 rounded-2xl p-8 not-prose">
                <h2 className="text-xl font-bold text-foreground mb-4">En résumé</h2>
                <p className="text-muted-foreground mb-4">
                  Le transport de bagages entre la France et le Maroc offre plusieurs options 
                  adaptées à chaque situation : du simple envoi de valises pour les vacances 
                  au déménagement complet. En préparant correctement vos bagages, en choisissant 
                  le mode de transport adapté et en optant pour un transporteur fiable, 
                  vous assurez la réception de vos effets en toute sérénité.
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <a href="/transport-colis-maroc-fiable" className="text-primary hover:underline">Transport fiable →</a>
                  <a href="/avis-transport-colis-france-maroc" className="text-primary hover:underline">Avis clients →</a>
                  <a href="/faq-transport-colis-maroc" className="text-primary hover:underline">FAQ →</a>
                </div>
              </section>

            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
};

export default TransportBagagesFranceMaroc;
